<script lang="ts">
	/**
	 * The seeds-and-growing assistant, as a floating panel.
	 *
	 * Posts to `/api/chat`, handled by this site's own Worker (`src/worker.ts`),
	 * same origin — the same arrangement as `contact/ContactForm.svelte`, which is
	 * the closest thing to a precedent here and worth reading alongside this.
	 *
	 * ## Client-only, on two locales
	 *
	 * Every page of this site is prerendered, so the panel is gated on `onMount`:
	 * nothing here belongs in the served HTML, and a chat window in a document a
	 * crawler reads is noise at best.
	 *
	 * It renders only for `en` and `th`. Those are the two locales with their own
	 * WooCommerce snapshot and therefore the only two whose prices the assistant
	 * can state — and the two whose answers anyone here can actually check. The
	 * other five carry the English UI strings so the message catalogues stay in
	 * step; nothing reads them.
	 *
	 * ## Two layouts, one component
	 *
	 * Below `sm` the panel is the whole screen: `inset-0`, square corners, the
	 * launcher hidden behind it, and the page behind it locked from scrolling. A
	 * 24rem card floating over a 360px phone is most of the viewport anyway, and
	 * the part of it that is not the card is a strip of page that scrolls under
	 * your thumb while you type. Above `sm` it is a card in the bottom-right
	 * corner, sitting above the launcher rather than on top of it.
	 *
	 * The composer carries `env(safe-area-inset-bottom)` because at full height
	 * on iOS it is the thing sitting under the home indicator.
	 *
	 * The full-screen layout is sized from `visualViewport` rather than from the
	 * viewport units, because those two part ways the moment the keyboard opens:
	 * a `fixed inset-0` panel keeps the height of the *layout* viewport, so the
	 * keyboard covers its bottom and its header is pushed off the top. Tracking
	 * the visual viewport keeps the whole panel — title, transcript, composer —
	 * inside whatever the keyboard leaves behind.
	 *
	 * ## The transcript lives here
	 *
	 * The Worker is stateless and the whole conversation is posted on every turn,
	 * which is why it caps length and revalidates every message. Anything the
	 * visitor sees here has already been through that.
	 */
	import { onMount, tick } from 'svelte';
	import { fade, fly } from 'svelte/transition';
	import { prefersReducedMotion } from 'svelte/motion';
	import { TURNSTILE_SITE_KEY } from '$lib/turnstile';
	import { m } from '$lib/paraglide/messages';
	import { renderAnswer } from './answer';

	let { locale }: { locale: string } = $props();

	interface Turn {
		role: 'user' | 'assistant';
		content: string;
	}

	const CHAT_LOCALES = ['en', 'th'];
	const endpoint = '/api/chat';
	const turnstileKey = TURNSTILE_SITE_KEY;

	let mounted = $state(false);
	let open = $state(false);
	let draft = $state('');
	let turns = $state<Turn[]>([]);
	let status = $state<'idle' | 'streaming' | 'error'>('idle');
	let turnstileToken = $state('');
	let challenge = $state<HTMLDivElement | null>(null);
	let transcript = $state<HTMLDivElement | null>(null);
	let input = $state<HTMLTextAreaElement | null>(null);

	const enabled = $derived(CHAT_LOCALES.includes(locale));
	/**
	 * Turnstile guards the first message only, matching the Worker. Without a site
	 * key configured the challenge is skipped there too, so the widget must not
	 * wait for a token that will never arrive.
	 */
	const needsToken = $derived(Boolean(turnstileKey) && turns.length === 0 && !turnstileToken);
	const canSend = $derived(draft.trim().length > 0 && status !== 'streaming' && !needsToken);
	const motion = $derived(prefersReducedMotion.current ? 0 : 1);

	const suggestions = $derived([
		m.chat_suggestion_1(),
		m.chat_suggestion_2(),
		m.chat_suggestion_3()
	]);

	onMount(() => {
		mounted = true;
	});

	/**
	 * Locks the page behind the panel while it is full-screen.
	 *
	 * Only below `sm`, where the panel covers the viewport: on desktop the page is
	 * still visible beside the card and freezing it would be wrong. Restored on
	 * close and on teardown, so a navigation mid-conversation cannot leave the
	 * site unscrollable.
	 */
	$effect(() => {
		if (!open) return;

		const phone = window.matchMedia('(max-width: 639px)');
		if (!phone.matches) return;

		const previous = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = previous;
		};
	});

	/**
	 * Fits the full-screen panel to the visual viewport, keyboard included.
	 *
	 * `top` follows `offsetTop` as well as `height` following `height`: on iOS the
	 * browser scrolls the visual viewport within the layout viewport to reveal the
	 * focused field, and a fixed element is placed against the layout viewport, so
	 * without the offset the panel drifts off the top of what you can see.
	 *
	 * Phones only, and only while open — the desktop card takes its size from the
	 * `sm:` classes, which an inline style would otherwise beat. Browsers without
	 * `visualViewport` keep the plain `inset-0` behaviour.
	 */
	let panelStyle = $state('');
	$effect(() => {
		if (!open) return;

		const viewport = window.visualViewport;
		if (!viewport) return;
		const phone = window.matchMedia('(max-width: 639px)');

		const fit = () => {
			if (!phone.matches) {
				panelStyle = '';
				return;
			}
			panelStyle = `top:${Math.round(viewport.offsetTop)}px;height:${Math.round(viewport.height)}px`;
			// The transcript just got shorter; keep the turn being answered in view.
			if (input && document.activeElement === input) void scrollToEnd();
		};

		fit();
		viewport.addEventListener('resize', fit);
		viewport.addEventListener('scroll', fit);
		phone.addEventListener('change', fit);
		return () => {
			viewport.removeEventListener('resize', fit);
			viewport.removeEventListener('scroll', fit);
			phone.removeEventListener('change', fit);
			panelStyle = '';
		};
	});

	/**
	 * Closes the panel when an answer's link is followed, on phones.
	 *
	 * There the panel *is* the screen, so a link that navigates behind it looks
	 * like nothing happened — the visitor is left staring at the same chat over a
	 * page they cannot see. On desktop the card sits beside a visible page and the
	 * conversation is worth keeping, so it stays open, same as the scroll lock.
	 *
	 * Delegated from the transcript because the answers are `{@html}`: their
	 * anchors are written by `renderAnswer`, not by the template, so there is no
	 * element here to put a handler on. Navigation itself is left alone — this
	 * only clears the panel out of the way of it.
	 */
	$effect(() => {
		if (!open || !transcript) return;

		const node = transcript;
		const close = (event: MouseEvent) => {
			if (event.defaultPrevented || event.button !== 0) return;
			if (!(event.target instanceof Element) || !event.target.closest('a')) return;
			if (!window.matchMedia('(max-width: 639px)').matches) return;
			open = false;
		};

		node.addEventListener('click', close);
		return () => node.removeEventListener('click', close);
	});

	/**
	 * Loads Turnstile the first time the panel opens, not on page load.
	 *
	 * The script is ~50 KB and the overwhelming majority of visitors never open
	 * this panel, so making every page pay for it would be the widget's largest
	 * cost by far — for a challenge most people never reach.
	 */
	let challengeRendered = false;
	$effect(() => {
		if (!open || !turnstileKey || !challenge || challengeRendered) return;
		challengeRendered = true;

		const render = () => {
			const turnstile = (
				window as unknown as { turnstile?: { render: (el: HTMLElement, opts: object) => void } }
			).turnstile;
			if (challenge) {
				turnstile?.render(challenge, {
					sitekey: turnstileKey,
					callback: (token: string) => (turnstileToken = token)
				});
			}
		};

		if ((window as unknown as { turnstile?: unknown }).turnstile) {
			render();
			return;
		}

		const script = document.createElement('script');
		script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
		script.async = true;
		script.onload = render;
		document.head.appendChild(script);
	});

	async function scrollToEnd() {
		await tick();
		if (transcript) transcript.scrollTop = transcript.scrollHeight;
	}

	/**
	 * Grows the composer with its content, up to a few lines.
	 *
	 * `scrollHeight` includes the field's own padding — the padding that centres a
	 * single line against the send button — so the height it yields is the height
	 * the field should have, not the height of the text alone.
	 */
	function autosize() {
		if (!input) return;
		input.style.height = 'auto';
		input.style.height = `${Math.min(input.scrollHeight, 120)}px`;
	}

	async function toggle() {
		open = !open;
		if (open) {
			await tick();
			input?.focus();
			await scrollToEnd();
		}
	}

	/**
	 * Sends the transcript and streams the answer back into the last turn.
	 *
	 * The assistant turn is appended empty and then grown, so the answer appears
	 * as it is written rather than after a silence. `turns` is reassigned rather
	 * than mutated in place because the array is `$state` and the reassignment is
	 * what the transcript's rendering depends on.
	 */
	async function send(text?: string) {
		const question = (text ?? draft).trim();
		if (!question || status === 'streaming' || needsToken) return;

		draft = '';
		if (input) input.style.height = 'auto';
		status = 'streaming';
		turns = [...turns, { role: 'user', content: question }, { role: 'assistant', content: '' }];
		await scrollToEnd();

		try {
			const response = await fetch(endpoint, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					locale,
					token: turnstileToken,
					// Without the empty assistant turn just appended, which is ours.
					messages: turns.slice(0, -1)
				})
			});

			if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);

			const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
			let buffer = '';

			while (true) {
				const { value, done } = await reader.read();
				if (done) break;

				buffer += value;
				// A frame can be split across chunks, so the tail is carried forward.
				const frames = buffer.split('\n\n');
				buffer = frames.pop() ?? '';

				for (const frame of frames) {
					const data = frame
						.split('\n')
						.filter((line) => line.startsWith('data:'))
						.map((line) => line.slice(5).trim())
						.join('');
					if (!data) continue;

					const event = JSON.parse(data) as { text?: string; done?: boolean; error?: boolean };
					if (event.error) throw new Error('stream');

					if (event.text) {
						const last = turns.at(-1);
						if (last) {
							last.content += event.text;
							turns = [...turns];
							await scrollToEnd();
						}
					}
				}
			}

			status = 'idle';
		} catch {
			// Drop the empty assistant turn — an error line below the transcript is
			// clearer than a blank bubble that never fills. The question is put back
			// in the composer so a failed send is one keystroke from being retried
			// rather than something the visitor has to retype.
			turns = turns.slice(0, -2);
			draft = question;
			status = 'error';
		}

		await scrollToEnd();
	}

	function onKeydown(event: KeyboardEvent) {
		// Enter sends, Shift+Enter breaks the line — what a chat window is expected
		// to do, and the reason the input is a textarea rather than a form.
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			void send();
		}
	}
</script>

<!--
	Escape closes, and the panel carries `role="dialog"` with a label — but
	deliberately not `aria-modal="true"`. That attribute tells a screen reader the
	rest of the page has been made inert, and nothing here traps focus or does so.
	On desktop the page really does stay interactive beside the card, and claiming
	otherwise would strand a screen-reader user in a dialog they can tab out of.
-->
<svelte:window
	onkeydown={(event) => {
		if (event.key === 'Escape' && open) open = false;
	}}
/>

{#if mounted && enabled}
	<!--
		z-index sits above the header's `z-30` and the `z-50` its dialogs reach,
		deliberately: this floats over everything, including an open mobile menu.
		See the stacking note in `Header/Header.svelte`.
	-->
	{#if open}
		<!--
			Scrim, phones only. The desktop card is small and the page around it stays
			usable, so dimming it there would imply a modality the panel does not have.
		-->
		<div
			class="bg-ink-600/40 fixed inset-0 z-[65] sm:hidden"
			transition:fade={{ duration: 150 * motion }}
			onclick={() => (open = false)}
			aria-hidden="true"
		></div>

		<div
			class="bg-surface fixed inset-0 z-[70] flex flex-col shadow-2xl
				sm:inset-auto sm:right-4 sm:bottom-24 sm:h-[min(34rem,calc(100vh-9rem))] sm:w-[25rem]
				sm:rounded-lg sm:ring-1 sm:ring-black/5 dark:sm:ring-white/10"
			style={panelStyle}
			role="dialog"
			aria-label={m.chat_title()}
			transition:fly={{ y: 16 * motion, duration: 200 * motion, opacity: 0 }}
		>
			<!-- Header -->
			<header
				class="border-subtle flex shrink-0 items-center gap-3 border-b px-4 py-3
					pt-[max(0.75rem,env(safe-area-inset-top))] sm:pt-3"
			>
				<span
					class="bg-brand-500 text-ink-500 flex size-9 shrink-0 items-center justify-center rounded-full"
					aria-hidden="true"
				>
					<svg viewBox="0 0 24 24" class="size-5" fill="none" stroke="currentColor" stroke-width="2">
						<path
							d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.9-.9L3 20.5l1.6-4.4A8.4 8.4 0 0 1 12 3.1a8.4 8.4 0 0 1 9 8.4Z"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>
				</span>

				<div class="min-w-0 flex-1">
					<h2 class="font-heading truncate text-base leading-tight">
						{m.chat_title()}
					</h2>
					<p class="text-muted truncate text-xs">{m.chat_subtitle()}</p>
				</div>

				<button
					type="button"
					class="text-muted hover:bg-surface-muted hover:text-body -mr-1 flex size-9 shrink-0
						items-center justify-center rounded-full transition-colors"
					onclick={toggle}
					aria-label={m.chat_close()}
				>
					<svg viewBox="0 0 24 24" class="size-5" fill="none" stroke="currentColor" stroke-width="2">
						<path d="m6 6 12 12M18 6 6 18" stroke-linecap="round" />
					</svg>
				</button>
			</header>

			<!-- Transcript -->
			<div bind:this={transcript} class="flex-1 space-y-4 overflow-y-auto px-4 py-4">
				<p class="text-body text-sm leading-relaxed">{m.chat_greeting()}</p>

				{#if turns.length === 0}
					<!--
						Starters, shown only on an empty transcript. A blank chat box asks
						the visitor to guess what it knows; three real questions answer that
						faster than any placeholder text can.
					-->
					<div class="flex flex-wrap gap-2 pt-1">
						{#each suggestions as suggestion (suggestion)}
							<button
								type="button"
								class="border-subtle text-muted hover:border-brand-500 hover:text-body
									rounded-full border px-3 py-1.5 text-left text-xs transition-colors
									disabled:opacity-50"
								disabled={needsToken}
								onclick={() => send(suggestion)}
							>
								{suggestion}
							</button>
						{/each}
					</div>
				{/if}

				{#each turns as turn, index (index)}
					{#if turn.role === 'user'}
						<p
							class="bg-surface-sunken text-body ml-auto w-fit max-w-[85%] rounded-lg
								rounded-br-sm px-3 py-2 text-sm leading-relaxed"
						>
							{turn.content}
						</p>
					{:else if turn.content}
						<div
							class="text-body [&_a]:text-brand-800 dark:[&_a]:text-brand-400 max-w-[95%]
								text-sm leading-relaxed [&_a]:underline [&_a]:underline-offset-2
								[&_strong]:font-semibold"
						>
							<!--
								The one place on this site where model output becomes markup.
								`renderAnswer` escapes the whole string before generating any
								tag, so nothing here can open one, and it turns a markdown link
								into an anchor only when the href is ours — see that file for
								why a prompt instruction is not sufficient on its own.
							-->
							<!-- eslint-disable-next-line svelte/no-at-html-tags -->
							{@html renderAnswer(turn.content)}
						</div>
					{/if}
				{/each}

				{#if status === 'streaming' && turns.at(-1)?.content === ''}
					<div class="flex items-center gap-1.5 py-1" aria-label={m.chat_thinking()}>
						{#each [0, 1, 2] as dot (dot)}
							<span
								class="bg-muted size-1.5 animate-bounce rounded-full"
								style="animation-delay: {dot * 140}ms"
							></span>
						{/each}
					</div>
				{/if}

				{#if status === 'error'}
					<p class="text-sm leading-relaxed text-red-600 dark:text-red-400">{m.chat_error()}</p>
				{/if}
			</div>

			<!-- Composer -->
			<div
				class="border-subtle shrink-0 border-t px-4 pt-3
					pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-3"
			>
				{#if needsToken}
					<div bind:this={challenge} class="mb-2"></div>
				{/if}

				<div
					class="border-subtle bg-surface focus-within:border-brand-500 flex items-end gap-2
						rounded-lg border px-3 py-2 transition-colors"
				>
					<textarea
						bind:this={input}
						bind:value={draft}
						oninput={autosize}
						onkeydown={onKeydown}
						rows="1"
						maxlength="1000"
						placeholder={m.chat_placeholder()}
						class="text-body placeholder:text-muted max-h-[120px] min-w-0 flex-1 resize-none
							bg-transparent py-[0.29rem] text-sm leading-relaxed focus:outline-none"
					></textarea>

					<button
						type="button"
						class="bg-brand-500 text-ink-500 hover:bg-brand-600 flex size-8 shrink-0
							items-center justify-center rounded-full transition-colors
							disabled:cursor-not-allowed disabled:opacity-40"
						disabled={!canSend}
						onclick={() => send()}
						aria-label={m.chat_send()}
					>
						<svg viewBox="0 0 24 24" class="size-4" fill="none" stroke="currentColor" stroke-width="2">
							<path d="M5 12h14M13 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
						</svg>
					</button>
				</div>

				<p class="text-muted mt-2 text-center text-[11px] leading-snug">
					{m.chat_disclaimer()}
				</p>
			</div>
		</div>
	{/if}

	<!--
		Launcher. Hidden behind the panel on phones, where the panel is the whole
		screen; on desktop it stays put and the card opens above it.
	-->
	<button
		type="button"
		class="bg-brand-500 text-ink-500 hover:bg-brand-600 fixed right-4 bottom-4 z-[60]
			items-center rounded-full shadow-lg transition-colors
			{open ? 'hidden sm:flex' : 'flex'}"
		onclick={toggle}
		aria-expanded={open}
		aria-label={m.chat_open()}
	>
		<span class="flex size-14 items-center justify-center sm:size-12">
			{#if open}
				<svg viewBox="0 0 24 24" class="size-6" fill="none" stroke="currentColor" stroke-width="2">
					<path d="m6 6 12 12M18 6 6 18" stroke-linecap="round" />
				</svg>
			{:else}
				<svg viewBox="0 0 24 24" class="size-6" fill="none" stroke="currentColor" stroke-width="2">
					<path
						d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.9-.9L3 20.5l1.6-4.4A8.4 8.4 0 0 1 12 3.1a8.4 8.4 0 0 1 9 8.4Z"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
				</svg>
			{/if}
		</span>
		<span class="font-heading hidden pr-5 text-sm font-bold tracking-wide uppercase sm:inline">
			{open ? m.chat_close() : m.chat_open()}
		</span>
	</button>
{/if}
